import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/hooks";
import { MdEvent } from "react-icons/md";
import dayjs from "dayjs";
import "add-to-calendar-button";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { formatDate, formatTime, formatWeekday } from "utils/format";
import Button from "components/button";
import Card from "components/card";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import AlertPane from "components/alert";
import Skeleton from "components/skeleton/Skeleton";
import { ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";

const STATE_MESSAGES = {
  confirmed: "Attendance confirmed",
  declined: "Attendance declined",
  na: "Response cleared",
};

const serviceTime = (time) => formatTime(`2000-01-01T${time}`);

const ServiceDetails = () => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const confirm = useConfirm();
  const [userService, setUserService] = useState();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pendingState, setPendingState] = useState(null);

  const params = useParams();
  const [queryParams] = useSearchParams();
  const token = queryParams.get("token");

  const loadService = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    AtriumApi.get(`/v1/volunteer/services/${params.id}?token=${token}`)
      .then((resp) => {
        setUserService(resp.data);
      })
      .catch((error) => setLoadError(error))
      .finally(() => setLoading(false));
  }, [params.id, token]);

  useEffect(() => {
    loadService();
  }, [loadService]);

  const backTo = `/volunteer/my-services?token=${token}`;

  if (loading || loadError || !userService) {
    return (
      <PageContainer>
        <PageHeader title="Service Details" backTo={backTo} backLabel="Back to Services" />
        {loading ? (
          <div className="flex flex-col gap-4">
            <Skeleton lines={2} className="w-64" />
            <Skeleton className="h-28 w-full rounded-card" />
          </div>
        ) : (
          <ErrorState
            message={getApiErrorMessage(loadError, "We couldn’t load this service.")}
            onRetry={loadService}
          />
        )}
      </PageContainer>
    );
  }

  const service = userService.service;

  const setState = (newState) => {
    setPendingState(newState);
    return AtriumApi.put(
      `/v1/volunteer/services/${userService.id}`,
      { state: newState },
      { params: { token } }
    )
      .then((resp) => {
        notify.success(STATE_MESSAGES[newState] || "Response saved");
        setUserService(resp.data);
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not update your response"));
      })
      .finally(() => setPendingState(null));
  };

  const dateString =
    service.schedule === "weekly"
      ? "Every " + formatWeekday(dayjs.utc(service.date).local(), "long")
      : formatDate(service.date);

  const timeRange = `${serviceTime(service.startAt)} to ${serviceTime(service.endAt)}`;

  const confirmAttendance = async () => {
    const confirmed = await confirm({
      title: `Attend "${service.name}"?`,
      message: `${dateString}, ${timeRange}${service.address ? ` at ${service.address}` : ""}.`,
      confirmLabel: "Confirm Attendance",
    });
    if (confirmed) setState("confirmed");
  };

  const showButtons =
    service?.schedule === "weekly" ||
    (service?.schedule === "once" &&
      dayjs(`${service?.date.split("T")[0]}T${service?.startAt}`).diff(dayjs(), "minute") >= 60);

  return (
    <PageContainer>
      <PageHeader
        title={service.name}
        description={`${dateString} · ${timeRange}${service.address ? ` · ${service.address}` : ""}`}
        backTo={backTo}
        backLabel="Back to Services"
      />

      <Card padding="md">
        <h2 className="text-heading">Details</h2>
        <p className="mt-2 text-sm text-fg-muted">
          {service.description || "No description provided."}
        </p>
      </Card>

      {userService.state === "confirmed" && (
        <AlertPane tone="success" className="">
          You’ve confirmed attendance.
        </AlertPane>
      )}

      {userService.state === "declined" && (
        <AlertPane tone="info" className="">
          You’ve declined attendance.
        </AlertPane>
      )}

      {showButtons && (
        <div className="flex flex-col gap-3 lg:flex-row">
          {userService.state === "na" && (
            <>
              <Button
                variant="secondary"
                size="lg"
                loading={pendingState === "declined"}
                onClick={() => setState("declined")}
              >
                Decline
              </Button>
              <Button
                size="lg"
                loading={pendingState === "confirmed"}
                onClick={confirmAttendance}
              >
                Confirm Attendance
              </Button>
            </>
          )}
          {userService.state === "confirmed" && (
            <>
              <Button
                variant="secondary"
                size="lg"
                loading={pendingState === "na"}
                onClick={() => setState("na")}
              >
                Cancel Confirmation
              </Button>
              <Button
                size="lg"
                leftIcon={<MdEvent aria-hidden="true" className="h-5 w-5" />}
                onClick={onOpen}
              >
                Add to Calendar
              </Button>
            </>
          )}
          {userService.state === "declined" && (
            <Button
              variant="secondary"
              size="lg"
              loading={pendingState === "na"}
              onClick={() => setState("na")}
            >
              Cancel Decline
            </Button>
          )}
        </div>
      )}

      <Dialog isOpen={isOpen} onClose={onClose} title="Add to Calendar">
        <add-to-calendar-button
          inline
          buttonStyle="text"
          name={service.name}
          description={service.description}
          startDate={new Date(service.date).toISOString().split("T")[0]}
          startTime={service.startAt}
          endTime={service.endAt}
          location={service.address}
          availability="free"
          options="'Apple','Google','Outlook.com', 'iCal'"
          trigger="click"
          buttonsList
          customLabels={`{"apple": "Apple Calendar", "google": "Google Calendar", "outlook.com": "Outlook"}`}
          pastDateHandling="disabled"
          recurrence={service.schedule === "weekly" ? "weekly" : undefined}
        />
      </Dialog>
    </PageContainer>
  );
};

export default ServiceDetails;
